/**
 * reprioritizationSessionPrefill — break 5 of the decision → commitment →
 * term/condition → signal → verdict → reprioritization chain audit.
 *
 * A reprioritization recommendation (ReprioritizationTriggerPanel) could be
 * acknowledged and closed, and led nowhere — there was no path from "the
 * plan needs to re-rank" to an actual re-ranking. This module supplies the
 * two things that path needs:
 *
 *   1. A SessionPrefill (src/lib/sessionPrefill.ts contract) built from the
 *      trigger's reason payload and the plan's current item list, for
 *      "Start a session to re-rank".
 *   2. Best-effort resolution of the prioritization Cycle (degap_cycles.id)
 *      a plan cycle's input traces back to, for "Open a catchball round" —
 *      the Cascade/Catchball surfaces are scoped to that cycle id, not to
 *      plan_cycles.id, which is a different domain (canon: planning vs
 *      prioritization are two separate cycle families, joined only by
 *      plan_cycles.input_reference_id).
 *
 * Both resolutions are honestly partial: an unresolvable input_type, a
 * missing input_reference_id, or an empty plan version all resolve to
 * null/empty rather than guessing. Callers disable the corresponding action
 * with a plain reason when that happens — see ReprioritizationTriggerPanel.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { SessionPrefill } from './sessionPrefill';

const MAX_ASK = 200;

/** Pull the human summary out of a trigger's `reason` jsonb, the same fields TriggerRow already reads. */
export function reprioritizationTriggerSummary(
  reason: Record<string, unknown> | null | undefined
): string {
  if (!reason || typeof reason !== 'object') return 'Re-rank priorities';
  const raw = reason.summary ?? reason.reason ?? reason.description;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : 'Re-rank priorities';
}

/** Build the sessionPrefill.ts contract object for "Start a session to re-rank". */
export function buildReRankSessionPrefill(
  reason: Record<string, unknown> | null | undefined,
  items: { id: string; title: string }[]
): SessionPrefill {
  const summary = reprioritizationTriggerSummary(reason);
  return {
    ask: `Re-rank priorities — ${summary}`.slice(0, MAX_ASK),
    items,
  };
}

export interface PlanCycleInputRef {
  inputType: string | null;
  inputReferenceId: string | null;
}

/**
 * Resolve the prioritization Cycle (degap_cycles.id) a plan cycle's input
 * traces back to, or null when it can't be resolved. Two known input
 * shapes (migration 054): `item_pool` → item_pools.cycle_id directly;
 * `published_priority_list` → priority_sets.cycle_id, falling back to
 * priority_proposals.cycle_id (mirrors resolveInputItemIds's own fallback
 * in planningService.ts). Any other input_type, or a missing reference,
 * returns null — never a guess.
 */
export async function resolvePrioritizationCycleId(
  input: PlanCycleInputRef
): Promise<string | null> {
  if (!input.inputReferenceId) return null;
  const db = getTypedSupabaseClient();

  if (input.inputType === 'item_pool') {
    const { data } = await db
      .from('item_pools')
      .select('cycle_id')
      .eq('id', input.inputReferenceId)
      .maybeSingle();
    return data?.cycle_id ?? null;
  }

  if (input.inputType === 'published_priority_list') {
    const { data: set } = await db
      .from('priority_sets')
      .select('cycle_id')
      .eq('id', input.inputReferenceId)
      .maybeSingle();
    if (set?.cycle_id) return set.cycle_id;

    const { data: proposal } = await db
      .from('priority_proposals')
      .select('cycle_id')
      .eq('id', input.inputReferenceId)
      .maybeSingle();
    return proposal?.cycle_id ?? null;
  }

  return null;
}

/**
 * The plan's current item list for a re-rank prefill: non-dropped
 * initiatives on the plan cycle's latest version. Empty when the cycle has
 * no version yet or no live initiatives — an honest "nothing to re-rank",
 * not an error.
 */
export async function resolvePlanItemsForReRank(
  planCycleId: string
): Promise<{ id: string; title: string }[]> {
  const db = getTypedSupabaseClient();
  const { data: versions } = await db
    .from('plan_versions')
    .select('id')
    .eq('plan_cycle_id', planCycleId)
    .order('version_number', { ascending: false })
    .limit(1);
  const latest = versions?.[0];
  if (!latest) return [];

  const { data: inits } = await db
    .from('initiatives')
    .select('item_id, title')
    .eq('plan_version_id', latest.id)
    .neq('status', 'dropped')
    .not('item_id', 'is', null);

  const seen = new Set<string>();
  const items: { id: string; title: string }[] = [];
  for (const row of inits ?? []) {
    if (!row.item_id || seen.has(row.item_id)) continue;
    seen.add(row.item_id);
    items.push({ id: row.item_id, title: row.title ?? `Item ${row.item_id.slice(0, 8)}` });
  }
  return items;
}
